import React, { useState } from 'react';
import { Badge, Button, Card, Pagination, Table } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { FaPen, FaSort, FaSortDown, FaSortUp } from 'react-icons/fa';
import {
  formatAssistenciaMoney,
  getAssistenciaUnitPrice,
} from '../../assistenciasCadastrosUtils';
import {
  getTableHeaderClass,
  getModernCardBodyClass,
  getModernCardFooterClass,
  getModernTableClass,
  getModernDataTableMetaClass,
} from 'utils/tableTheme';

const Results = ({ data, currentTheme, canWrite }) => {
  const history = useHistory();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(40);
  const [orderBy, setOrderBy] = useState('nome');
  const [order, setOrder] = useState('asc');

  const totalPages = Math.max(1, Math.ceil(data.length / rowsPerPage));

  const getPaginationRange = () => {
    const start = Math.max(page - 2, 0);
    const end = Math.min(page + 2, totalPages - 1);
    const range = [];
    for (let i = start; i <= end; i += 1) {
      range.push(i);
    }
    return range;
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const renderSortIcon = (column) => {
    if (column !== orderBy) {
      return <FaSort style={{ marginLeft: 5, opacity: 0.5 }} />;
    }
    return order === 'asc'
      ? <FaSortUp style={{ marginLeft: 5, opacity: 1 }} />
      : <FaSortDown style={{ marginLeft: 5, opacity: 1 }} />;
  };

  const getSortedData = (items) => {
    const stabilized = items.map((el, index) => [el, index]);

    stabilized.sort((a, b) => {
      let itemA = a[0][orderBy] ?? '';
      let itemB = b[0][orderBy] ?? '';

      if (orderBy === 'valor') {
        itemA = getAssistenciaUnitPrice(a[0]);
        itemB = getAssistenciaUnitPrice(b[0]);
      }

      if (itemA < itemB) return order === 'asc' ? -1 : 1;
      if (itemA > itemB) return order === 'asc' ? 1 : -1;
      return a[1] - b[1];
    });

    return stabilized.map((el) => el[0]);
  };

  const sortedData = getSortedData(data);
  const paginatedData = sortedData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const handleRowClick = (id) => {
    history.push(`/assistencias/${id}`);
  };

  const handleEditClick = (event, id) => {
    event.stopPropagation();
    history.push(`/assistencias/edit/${id}`);
  };

  return (
    <div>
      <p className={getModernDataTableMetaClass()}>
        {data.length} registros encontrados. Página {page + 1} de {totalPages}
      </p>

      <Card
        className={
          currentTheme === 'dark' ? 'bg-dark text-light border-secondary' : 'bg-light text-dark'
        }
      >
        <Card.Header
          className={
            currentTheme === 'dark' ? 'bg-dark text-light border-secondary' : 'bg-light text-dark'
          }
        >
          Assistências
        </Card.Header>
        <Card.Body
          className={getModernCardBodyClass(currentTheme)}
        >
          {data.length === 0 ? (
            <p className="text-center text-muted mb-0 py-4">Nenhuma assistência cadastrada.</p>
          ) : (
            <Table
              style={{ fontSize: '0.9rem' }}
              hover
              responsive
              size="sm"
              className={getModernTableClass(currentTheme)}
            >
              <thead>
                <tr className={getTableHeaderClass(currentTheme)}>
                  <th className="text-center">Logo</th>
                  <th
                    onClick={() => handleRequestSort('nome')}
                    style={{ cursor: 'pointer' }}
                  >
                    NOME {renderSortIcon('nome')}
                  </th>
                  <th
                    className="text-center"
                    onClick={() => handleRequestSort('cidade')}
                    style={{ cursor: 'pointer' }}
                  >
                    CIDADE/UF {renderSortIcon('cidade')}
                  </th>
                  <th
                    className="text-center"
                    onClick={() => handleRequestSort('email')}
                    style={{ cursor: 'pointer' }}
                  >
                    E-MAIL {renderSortIcon('email')}
                  </th>
                  <th className="text-center">E-MAIL HP</th>
                  <th
                    className="text-center"
                    onClick={() => handleRequestSort('valor')}
                    style={{ cursor: 'pointer' }}
                  >
                    CHAVE ASSIST. {renderSortIcon('valor')}
                  </th>
                  <th
                    className="text-center"
                    onClick={() => handleRequestSort('status')}
                    style={{ cursor: 'pointer' }}
                  >
                    STATUS {renderSortIcon('status')}
                  </th>
                  {canWrite && <th className="text-center">AÇÕES</th>}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item) => {
                  const isAtivo = item.status === 'ativo';
                  return (
                    <tr
                      key={item._id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleRowClick(item._id)}
                    >
                      <td className="text-center align-middle" style={{ width: 56 }}>
                        {item.logo ? (
                          <img
                            src={item.logo}
                            alt=""
                            style={{
                              width: 40,
                              height: 40,
                              objectFit: 'contain',
                              borderRadius: 4,
                            }}
                          />
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="pt-2 align-middle fw-semibold">{item.nome || '—'}</td>
                      <td className="text-center pt-2 pb-2 align-middle">
                        {item.cidade || '—'}/{item.estado || '—'}
                      </td>
                      <td className="text-center pt-2 pb-2 align-middle">{item.email || '—'}</td>
                      <td className="text-center pt-2 pb-2 align-middle">
                        {item.emailhp || '—'}
                      </td>
                      <td className="text-center pt-2 pb-2 align-middle">
                        {formatAssistenciaMoney(item, getAssistenciaUnitPrice(item))}
                      </td>
                      <td className="text-center pt-2 pb-2 align-middle">
                        <Badge bg={isAtivo ? 'success' : 'secondary'}>
                          {isAtivo ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </td>
                      {canWrite && (
                        <td className="text-center pt-2 pb-2 align-middle">
                          <Button
                            size="sm"
                            variant="outline-success"
                            onClick={(e) => handleEditClick(e, item._id)}
                          >
                            <FaPen className="me-1" />
                            Editar
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>

        {data.length > 0 && (
          <Card.Footer
            className={getModernCardFooterClass(currentTheme)}
          >
            <div>
              <label>
                Linhas por página:{' '}
                <select className="modern-data-table__rows-select"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(0);
                  }}
                >
                  {[20, 40, 60].map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <Pagination className="custom-pagination mb-0" variant="dark">
              <Pagination.Prev
                onClick={() => setPage(page - 1)}
                disabled={page === 0}
              />
              {getPaginationRange().map((index) => (
                <Pagination.Item
                  key={index}
                  active={index === page}
                  onClick={() => setPage(index)}
                >
                  {index + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages - 1}
              />
            </Pagination>
          </Card.Footer>
        )}
      </Card>
    </div>
  );
};

export default Results;
